import type { ResearchSessionStatus } from '@repo/contracts';

import { reportPath } from '../research-report/use-report';

/**
 * Where a session is in its life, as the lists show it — from nothing yet,
 * through a room taking questions, to a finished report — and the one thing
 * to do next from each. Worked out from `ResearchSessionStatus`; a session
 * with no status has never had a room opened.
 */
export type SessionStage = 'failed' | 'live' | 'new' | 'ready' | 'writing';

export interface SessionNext {
  stage: SessionStage;
  label: string;
  /** The button: what it says and where it goes. */
  action: string;
  href: string;
}

export function sessionNext(
  sessionId: string,
  status: ResearchSessionStatus | undefined,
): SessionNext {
  const board = `/sessions/${sessionId}`;
  if (!status) {
    return {
      action: 'Get feedback',
      href: board,
      label: 'Not started',
      stage: 'new',
    };
  }
  if (status.state === 'live') {
    return {
      action: 'Continue',
      href: board,
      label: 'In progress',
      stage: 'live',
    };
  }
  const report = status.report;
  if (report?.status === 'done') {
    return {
      action: 'Open report',
      href: reportPath({ id: report.roomId, sessionId }),
      label: 'Report ready',
      stage: 'ready',
    };
  }
  if (report?.status === 'failed') {
    return {
      action: 'Try again',
      href: board,
      label: 'Report failed',
      stage: 'failed',
    };
  }
  return {
    action: 'Open',
    href: board,
    label: 'Writing report',
    stage: 'writing',
  };
}

/** "12 questions · 2 screens" — how far the research got. */
export function progressLine(
  status: ResearchSessionStatus | undefined,
): string {
  if (!status) return 'No questions yet';
  const parts = [
    `${String(status.rounds)} question${status.rounds === 1 ? '' : 's'}`,
  ];
  if (status.rooms > 1) parts.push(`${String(status.rooms)} screens`);
  return parts.join(' · ');
}
